import * as React from 'react';
import client from '../api/contentful';
import { Entry, EntryCollection } from 'contentful';

export interface IProjectType {
  name: string;
  slug: string;
  description: string;
  projects: ProjectEntry[];
  metaImage: Entry<IImage>;
}

export type ProjectTypeEntry = Entry<IProjectType>;

interface IProject {
  name: string;
  slug: string;
  archived?: boolean;
  ProjectContent: ProjectContent[];
}

interface IImage {
  description: string;
  file: {
    url: string;
    contentType: string;
    details: {
      image: {
        height: number;
        width: number;
      };
    };
  };
}

export type ProjectEntry = Entry<IProject>;

interface IProjectContent {
  name: string;
}

export type ProjectContent = Entry<IProjectContent>;

export default class ProjectType {
  static data: EntryCollection<IProjectType>;

  static typeMap: Map<string, ProjectTypeEntry> = new Map();

  static typeProjectsMap: Map<string, ProjectEntry[]> = new Map();

  static projectContentMap: Map<string, ProjectContent[]> = new Map();

  static projectMap: Map<string, ProjectEntry> = new Map();

  static inFlightPromise: Promise<typeof ProjectType.data>;

  static getData(): Promise<typeof ProjectType.data> {
    if (ProjectType.inFlightPromise) {
      return ProjectType.inFlightPromise;
    }

    const promise = client
      .getEntries<IProjectType>({ content_type: 'projectType' })
      .then(data => {
        ProjectType.data = data;
        data.items.forEach(item => {
          ProjectType.typeMap.set(item.fields.slug, item);
          ProjectType.typeProjectsMap.set(
            item.fields.slug,
            item.fields.projects
          );
          //tslint:disable
          if (!item.fields.projects) {
            console.warn(
              `WARNING: ${item.fields.name} does not have any projects.`
            );
            return;
          }
          item.fields.projects.forEach(project => {
            if (!project.fields) {
              return;
            }

            ProjectType.projectMap.set(project.fields.slug, project);

            ProjectType.projectContentMap.set(
              project.fields.slug,
              project.fields.ProjectContent
            );
          });
        });

        return data;
      });
    ProjectType.inFlightPromise = promise;

    return promise;
  }
}

export interface IRenderProps {
  data: EntryCollection<IProjectType>;
  typeProjectsMap: Map<string, ProjectEntry[]>;
  projectContentMap: Map<string, ProjectContent[]>;
  typeMap: Map<string, ProjectTypeEntry>;
  projectMap: Map<string, ProjectEntry>;
  [key: string]: any;
}

interface IProps {
  render:
    | ((props: IRenderProps) => React.ReactNode)
    | React.ComponentClass<IRenderProps>;
  fallback?: React.SFC | React.ComponentClass;
  [key: string]: any;
}

interface IState {
  data: EntryCollection<IProjectType> | undefined;
}

function isComponentClass(render: any): render is React.ComponentClass {
  return undefined !== render.prototype.isReactComponent;
}

const blackList = new Set(['render', 'fallback', 'slug']);

export class ProjectTypeRenderer extends React.PureComponent<IProps, IState> {
  state: IState = {
    data: undefined,
  };

  componentDidMount() {
    ProjectType.getData().then(data => this.setState({ data }));
  }

  render() {
    const extraProps = Object.keys(this.props).reduce((acc, key) => {
      if (!blackList.has(key)) {
        acc[key] = this.props[key];
      }
      return acc;
    }, {});

    if (this.state.data) {
      if (isComponentClass(this.props.render)) {
        const Render = this.props.render;

        return (
          <Render
            data={this.state.data}
            typeProjectsMap={ProjectType.typeProjectsMap}
            projectContentMap={ProjectType.projectContentMap}
            typeMap={ProjectType.typeMap}
            projectMap={ProjectType.projectMap}
            {...extraProps}
          />
        );
      }

      return this.props.render({
        data: this.state.data,
        typeProjectsMap: ProjectType.typeProjectsMap,
        projectContentMap: ProjectType.projectContentMap,
        typeMap: ProjectType.typeMap,
        projectMap: ProjectType.projectMap,
        ...extraProps,
      });
    }

    if (this.props.fallback) {
      const Fallback = this.props.fallback;

      return <Fallback />;
    }

    return null;
  }
}



// WEBPACK FOOTER //
// ./src/models/ProjectType.tsx